import { CommonDialogs } from "@mcleod/common";
import { Button, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DropdownSelectionEvent, EditRowDecorator, Image, Layout, LookupModelSearchEvent, Snackbar, Table, TableRow, Toast } from "@mcleod/components";
import { Api, McLeodClassicIntegration, ModelRow, Navigation } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutBrokerageLTLControl } from "./autogen/AutogenLayoutBrokerageLTLControl";
import { UomExcludedExceptionSlideout } from "./UomExcludedExceptionSlideout";

const TARIFF_TYPE_SELL = 'S';
const TARIFF_TYPE_BUY = 'B';
const RATE_TARIFF_NEVER = 'N';
const RATE_TARIFF_ALWAYS = 'A';
const RATE_TARIFF_LTL = 'L';
const RATE_TARIFF_PARTIAL = 'P';
const RATE_TARIFF_ORDER = 'O';
const RATE_TARIFF_REVENUE = 'R';

const DOCUMENT_TYPE_LTL_QUOTE = "Y";

export class BrokerageLTLControl extends AutogenLayoutBrokerageLTLControl {
    private layoutExcludedUOMException = Layout.getLayout("lme/general/UomExcludedExceptionSlideout") as UomExcludedExceptionSlideout;
    private buttonAddRateException: Button;
    private imageDeleteRateException: Image;

    async onLoad() {
        this.setupTableButtons();
    }

        private setupTableButtons(): void {
        this.buttonAddRateException = new Button({
            color: "primary",
            caption: 'Add Calculation Method',
            imageName: ImageName.add,
            enabled: true,
            onClick: () => this.addRateExceptionTariffUom()
        });
        this.imageDeleteRateException = new Image({
            name: ImageName.delete,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: 'Remove Rate Exception',
            enabled: false,
            marginLeft: 16,
            marginTop: 1,
            onClick: () => this.updateRateExceptionTariffUom()
        });
        this.tableBrokerageLTLRateExceptions.addRightTool(this.buttonAddRateException);
        this.tableBrokerageLTLRateExceptions.addCenterTool(this.imageDeleteRateException);
        this.tableBrokerageLTLRateExceptions.addSelectionListener(() => {
            const selectedRowCount = this.tableBrokerageLTLRateExceptions.selectedRowCount;
            const selectedRow = this.tableBrokerageLTLRateExceptions.selectedRow as TableRow;
            const fieldCode = selectedRow?.data?.get("field_code");
            this.imageDeleteRateException.enabled = selectedRowCount === 1 ? true : false;
            this.imageDeleteRateException.color = selectedRowCount === 1 ? 'primary' : 'default.lightest';
            this.imageDeleteRateException.tooltip = selectedRowCount === 1 ? `Remove Rate Exception - ${fieldCode}` : null;
        });
    }

    addRateExceptionTariffUom(): void {
        const layout = this.layoutExcludedUOMException;
        new EditRowDecorator({
            title: `Add Calculation Method`,
            layout: layout,
            fillVerticalSpace: true,
            width: '60%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            layoutLoadListeners: async () => {
                const rateExceptionUomList: string = this.mainDataSource?.activeRow?.get("rate_exception_uom_list");
                const input = { "rate_exception_uom_list": rateExceptionUomList, "operation": "Excluded" };
                layout.tableBrokerageLTLRateExceptions.dataSource.search(input)
            },
            addlComponents: this.addRateExceptions(layout),
            onClose: async (cancelled: boolean) => {
                const slideoutTable = layout.tableBrokerageLTLRateExceptions as Table;
                const selectedTableRowData = slideoutTable.selectedRows.map((tableRow: TableRow) => tableRow.data);
                const selectedTableRowLength = selectedTableRowData.length;
                if (selectedTableRowLength === 0) {
                    layout.slideOut();
                    return;
                }
                const message = "Unsaved changes are present. Do you wish to leave without saving changes?";
                const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
                const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
                if (leave !== true) {
                    return;
                }
                layout.slideOut();
                this.mainDataSource.search();
            }
        });
    }

    async updateRateExceptionTariffUom(): Promise<void> {
        const selectedRow = this.tableBrokerageLTLRateExceptions.selectedRow;
        if (selectedRow == null) {
            Snackbar.showWarningSnackbar("Please select the Table row to perform operation.")
            return;
        }
        const fieldCode = selectedRow?.data?.get("field_code");
        const rateExceptionUomList: string = this.mainDataSource?.activeRow?.get("rate_exception_uom_list");
        const input = { "rate_exception_uom_list": rateExceptionUomList, "delete_field_code": fieldCode};
        try {
            const response = await Api.update("lme/general/rate-exception-dispatch-control", input);
            const data = response?.data[0];
            if (data) {
                const message: string = data?.message;
                const isSuccess: boolean = data?.success;
                if (isSuccess) {
                    Toast.showToast(message);
                    this.mainDataSource.search();
                }
            }
        } catch (error) {
           CommonDialogs.showError(error);
        }
    }

    addRateExceptions(layout: UomExcludedExceptionSlideout): Button{
        const addButton = new Button({
            backgroundColor: "primary", borderColor: "primary", borderWidth: 1,
            caption: "Add", color: "primary.reverse", rowBreak: false,
            onClick: async () =>  {
                try {
                    addButton.busy = true;
                    addButton.enabled = false;
                    const slideoutTable = layout.tableBrokerageLTLRateExceptions as Table;
                    const selectedTableRowData = slideoutTable.selectedRows.map((tableRow: TableRow) => tableRow.data);
                    const selectedTableRowLength = selectedTableRowData.length;
                    if (selectedTableRowLength === 0) {
                        CommonDialogs.showError("Please Select one or more to Add as Rate Exception");
                        return;
                    }
                    const fieldCodes = selectedTableRowData.map((row: ModelRow) => row.get("field_code"));
                    const newExceptionCode = fieldCodes.join(', ');
                    const rateExceptionUomList: string = this.mainDataSource?.activeRow?.get("rate_exception_uom_list");
                    const input = { "rate_exception_uom_list": rateExceptionUomList, "selected_rows": selectedTableRowLength, "new_calculation_methods":  newExceptionCode};
                    const response = await Api.post("lme/general/rate-exception-dispatch-control", input);
                    const data = response?.data[0];
                    if (data) {
                        const message = data?.message;
                        Toast.showToast(message);
                        await this.delay(3000);
                        layout.slideOut();
                        this.mainDataSource.search();
                    }
                }
                catch (error) {
                    CommonDialogs.showError(error);
                }
                finally {
                    addButton.busy = false;
                    addButton.enabled = true;
                }
            }
        });
        return addButton;
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    textboxBrkQuoteTemplateBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.document_type = DOCUMENT_TYPE_LTL_QUOTE;
        event.filter.template_number = ">0";
    }

    buttonLTLQuoteFormatButtonOnClick(event: ClickEvent) {
        const rateConfFormat = this.activeRow?.get("brk_quote_template") as string;
        const screenName = "com.tms.client.loadmaster.drs.EntryReportTemplate";
        McLeodClassicIntegration.openClassicScreen(screenName,
            "<ext> search report_template.template_number='" + rateConfFormat + "' and report_template.document_type='Y'");
    }

    textboxDefaultSellTariffIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.tariff_type_code = TARIFF_TYPE_SELL;
    }

    textboxDefaultBuyTariffIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.tariff_type_code = TARIFF_TYPE_BUY;
    }

    textboxBlanketSellTariffIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.tariff_type_code = TARIFF_TYPE_SELL;
    }

    textboxBlanketBuyTariffIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.tariff_type_code = TARIFF_TYPE_BUY;
    }

    textboxRateUsingTariffAfterDropdownSelection(event: DropdownSelectionEvent) {
        if (event.hasSelection()) {
            const rateUsingTariffWhen = event.selection.value;
            this.shouldShowTariffTypeCodes(rateUsingTariffWhen, true);
        }
    }

    async sourceDispatchControlAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.UPDATE) {
            Navigation.reloadCurrentPage(true);
        }
        const rateUsingTariffWhen = this.mainDataSource.activeRow?.get("rate_using_tariff");
        this.shouldShowTariffTypeCodes(rateUsingTariffWhen, false);
        const rateExceptionUomList: string = this.mainDataSource?.activeRow?.get("rate_exception_uom_list");
        const input = { "rate_exception_uom_list": rateExceptionUomList, "operation": "Include" };
        this.tableBrokerageLTLRateExceptions.dataSource.search(input)
    }

    private shouldShowTariffTypeCodes(rateUsingTariffWhen: string, eventDropDown: boolean) {
        const hideTariffCodes = [RATE_TARIFF_NEVER, RATE_TARIFF_ALWAYS, RATE_TARIFF_LTL, RATE_TARIFF_PARTIAL].includes(rateUsingTariffWhen);
        if (hideTariffCodes) {
            this.textboxRateUsingTariffCodes.visible = false;
            this.textboxRateUsingTariffCodes.required = false;
            this.textboxRateUsingTariffCodes.caption = "In Types";
            if (eventDropDown) {
                this.mainDataSource.activeRow.set("rate_using_tariff_codes", null);
            }
        }
        if (RATE_TARIFF_ORDER === rateUsingTariffWhen) {
            this.textboxRateUsingTariffCodes.visible = true;
            this.textboxRateUsingTariffCodes.required = true;
            this.textboxRateUsingTariffCodes.caption = "In Types";
            this.textboxRateUsingTariffCodes.tooltip = "Comma separated list of order types for which to apply tariff rating";
        }
        if (RATE_TARIFF_REVENUE === rateUsingTariffWhen) {
            this.textboxRateUsingTariffCodes.visible = true;
            this.textboxRateUsingTariffCodes.required = true;
            this.textboxRateUsingTariffCodes.caption = "In Codes";
            this.textboxRateUsingTariffCodes.tooltip = "Comma separated list of revenue codes for which to apply tariff rating";
        }
    }

}
